import { Injectable } from '@angular/core';

interface Scripts {
  name: string;
  src: string;
  attributes?: ObjectPair[];
}

interface ObjectPair {
  key: string;
  value: '';
}

export enum LoadingScripts {
  Stripe = 'stripe',
  Square = 'square',
  SquareSandbox = 'square-sandbox',
  ShowpassWidget = 'showpass-widget',
  GoogleMapApi = 'maps-googleapis',
  MapboxGL = 'mapbox-gl',
  MapboxSDK = 'mapbox-sdk',
  FestivalView = 'festivalView',
  WeeklyView = 'weeklyView',
  ClasspackView = 'classpackView',
  CustomPageView = 'customPageView',
  BeamerTrigger = 'beamerTrigger',
}

interface LoadScript {
  script?: string;
  loaded: boolean;
  status?: string;
  src?: string;
  message?: string;
  attributes?: ObjectPair[];
}

export const ScriptStore: Scripts[] = [
  { name: 'stripe', src: 'https://js.stripe.com/v3/' },
  { name: 'stripe-connect', src: 'https://connect-js.stripe.com/v1.0/connect.js' },
  { name: 'square', src: 'https://web.squarecdn.com/v1/square.js' },
  { name: 'square-sandbox', src: 'https://sandbox.web.squarecdn.com/v1/square.js' },
  { name: 'showpass-widget', src: 'https://connect.showpass.com/widget/' },
  {
    name: 'maps-googleapis',
    src:
      'https://maps.googleapis.com/maps/api/js?key=AIzaSyB6SXFU_D9Df7Wzt8dZl4fiE6E23VOtzWA&loading=async&callback=initMap&libraries=places',
    attributes: [{ key: 'defer', value: '' }, { key: 'async', value: '' }],
  },
  { name: 'mapbox-gl', src: 'https://api.mapbox.com/mapbox-gl-js/v3.1.2/mapbox-gl.js' },
  { name: 'mapbox-sdk', src: 'https://unpkg.com/@mapbox/mapbox-sdk/umd/mapbox-sdk.min.js' },
  {
    name: 'festivalView',
    src: 'https://fast.wistia.com/embed/medias/kwnloc0xbu.jsonp',
    attributes: [{ key: 'async', value: '' }],
  },
  {
    name: 'weeklyView',
    src: 'https://fast.wistia.com/embed/medias/ehmh64rx0m.jsonp',
    attributes: [{ key: 'async', value: '' }],
  },
  {
    name: 'classpackView',
    src: 'https://fast.wistia.com/embed/medias/9gaxjpbnh5.jsonp',
    attributes: [{ key: 'async', value: '' }],
  },
  {
    name: 'customPageView',
    src: 'https://fast.wistia.com/assets/external/E-v1.js',
    attributes: [{ key: 'async', value: '' }],
  },
  {
    name: 'beamerTrigger',
    src: 'https://app.getbeamer.com/js/beamer-embed.js',
    attributes: [{ key: 'defer', value: '' }],
  },
];

declare var document: any;

@Injectable({
  providedIn: 'root',
})
export class DynamicScriptLoaderService {
  private scripts: LoadScript = <LoadScript>{};
  private placesReadyPromise: Promise<any> | null = null;
  private mapsInitPromise: Promise<void> | null = null;

  constructor() {
    ScriptStore.forEach((script: Scripts) => {
      this.scripts[script.name] = <LoadScript>{
        loaded: false,
        src: script.src,
        attributes: script.attributes,
      };
    });
  }

  public load(...scripts: string[]): Promise<LoadScript[]> {
    const promises: any[] = [];
    scripts.forEach(script => promises.push(this.loadScript(script)));
    return Promise.all(promises);
  }

  public loadScript(name: string): Promise<LoadScript> {
    return new Promise(resolve => {
      const currentScript: LoadScript = this.scripts[name];

      if (name === 'maps-googleapis') {
        const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
        if (existingScript) {
          currentScript.loaded = true;
          this.waitForMapsInit()
            .then(() => resolve({ script: name, loaded: true, status: 'Already in DOM' }))
            .catch(() => resolve({ script: name, loaded: true, status: 'Already in DOM' }));
          return;
        }
      }

      if (!currentScript.loaded) {
        let script: HTMLScriptElement & {
          readyState?: string;
          onreadystatechange?: (event: Event) => void;
        } = document.createElement('script');

        script.type = 'text/javascript';
        script.src = currentScript.src;

        if (currentScript.attributes) this.setAttributes(script, currentScript.attributes);

        if (script.readyState) {
          script.onreadystatechange = () => {
            if (script.readyState === 'loaded' || script.readyState === 'complete') {
              script.onreadystatechange = null;
              currentScript.loaded = true;
              resolve({ script: name, loaded: true, status: 'Loaded' });
            }
          };
        } else {
          script.onload = () => {
            currentScript.loaded = true;
            if (name === 'maps-googleapis') {
              this.waitForMapsInit()
                .then(() => resolve({ script: name, loaded: true, status: 'Loaded' }))
                .catch(() => resolve({ script: name, loaded: true, status: 'Loaded' }));
              return;
            }
            resolve({ script: name, loaded: true, status: 'Loaded' });
          };
        }
        script.onerror = (error: any) =>
          resolve({ script: name, loaded: false, status: 'Loaded', message: error.message });
        document.getElementsByTagName('head')[0].appendChild(script);
      } else {
        if (name === 'maps-googleapis') {
          this.waitForMapsInit()
            .then(() => resolve({ script: name, loaded: true, status: 'Already Loaded' }))
            .catch(() => resolve({ script: name, loaded: true, status: 'Already Loaded' }));
          return;
        }
        resolve({ script: name, loaded: true, status: 'Already Loaded' });
      }
    });
  }

  private waitForMapsInit(timeoutMs = 15000): Promise<void> {
    if (this.mapsInitPromise) {
      return this.mapsInitPromise;
    }

    this.mapsInitPromise = new Promise((resolve, reject) => {
      const started = Date.now();

      const attempt = () => {
        const maps = typeof window !== 'undefined' ? (window as any).google?.maps : null;
        if (maps && typeof maps.importLibrary === 'function') {
          resolve();
          return;
        }

        if (Date.now() - started >= timeoutMs) {
          reject(new Error('Google maps bootstrap did not finish'));
          return;
        }

        setTimeout(attempt, 50);
      };

      attempt();
    });

    return this.mapsInitPromise;
  }

  private setAttributes(el: HTMLElement, attrs: ObjectPair[]): void {
    for (let index = 0; index < attrs.length; index++) {
      const element: ObjectPair = attrs[index];
      el.setAttribute(element.key, element.value);
    }
  }

  /**
   * google.maps can exist as an async bootstrap stub before Places is actually
   * initialized. Wait for importLibrary('places') so widgets like
   * <gmp-place-autocomplete> are safe to construct.
   */
  public whenPlacesReady(timeoutMs = 15000): Promise<any> {
    if (this.placesReadyPromise) {
      return this.placesReadyPromise;
    }

    this.placesReadyPromise = this.waitForMapsInit(timeoutMs)
      .then(async () => {
        const maps = typeof window !== 'undefined' ? (window as any).google?.maps : null;
        if (!maps || typeof maps.importLibrary !== 'function') {
          throw new Error('Google maps Places library did not load');
        }

        const placesLib = await maps.importLibrary('places');
        if (placesLib?.PlaceAutocompleteElement && typeof customElements !== 'undefined') {
          await Promise.race([
            customElements.whenDefined('gmp-place-autocomplete'),
            new Promise(resolve => setTimeout(resolve, 5000)),
          ]);
        }
        return placesLib;
      })
      .catch(error => {
        this.placesReadyPromise = null;
        throw error;
      });

    return this.placesReadyPromise;
  }
}

// Load only for getting error from Google Map API(Required Callback)
function initMap(): void {}
declare global {
  interface Window {
    initMap: () => void;
  }
}
window.initMap = initMap;
export {};
